import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { map, tap, finalize, share } from 'rxjs/operators';
import { DashboardResponseDto, DashboardDataDto } from '../../../shared/models/dashboard.dto';

/** Normaliza la respuesta de GET /api/dashboard/: con o sin envolvente ApiResponse. */
function normalizeDashboardResponse(body: DashboardResponseDto | DashboardDataDto): DashboardResponseDto {
  if ('data' in body && body.data != null && typeof body.data === 'object') {
    return body as DashboardResponseDto;
  }
  return {
    status: 'success',
    message: '',
    data: body as DashboardDataDto,
    errors: {}
  };
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private readonly apiUrl = `${environment.apiHost}/api`;
  private refreshDashboardSubject = new Subject<void>();
  private cachedData: DashboardDataDto | null = null;
  private pendingRequest: Observable<DashboardResponseDto> | null = null;

  // Observable to notify when dashboard data should be refreshed
  public refreshDashboard$ = this.refreshDashboardSubject.asObservable();

  constructor(private readonly http: HttpClient) { }

  /** Cache del último dashboard (layout/home comparten una sola llamada y home puede usar cache). */
  public getCachedDashboardData(): DashboardDataDto | null {
    return this.cachedData;
  }

  // The HTTP interceptor will automatically add the authorization token
  // Sirve desde cache si ya se cargó en esta sesión (p.ej. layout de org y layout de
  // proyecto comparten los mismos datos); usar triggerRefresh()/clearCache() para forzar un refetch.
  // Deduplica: si layout y home llaman a la vez, se hace una sola petición.
  // Acepta respuesta con envolvente { data: { organization, user, projects, total_projects } } o body directo.
  public getDashboardData(): Observable<DashboardResponseDto> {
    if (this.cachedData) {
      return of({ status: 'success', message: '', data: this.cachedData, errors: {} });
    }
    if (!this.pendingRequest) {
      this.pendingRequest = this.http.get<DashboardResponseDto | DashboardDataDto>(`${this.apiUrl}/dashboard/`).pipe(
        map(normalizeDashboardResponse),
        tap(res => { this.cachedData = res.data; }),
        finalize(() => { this.pendingRequest = null; }),
        // The layout and the page both ask while the first call is in flight; without this each
        // subscription fires its own request, and their responses land in either order.
        share()
      );
    }
    return this.pendingRequest;
  }

  // Emit event to trigger dashboard refresh (limpia cache para que el próximo getDashboardData sea fresco)
  public triggerRefresh(): void {
    this.cachedData = null;
    this.refreshDashboardSubject.next();
  }

  /** Clear cached dashboard data (e.g. on logout). */
  public clearCache(): void {
    this.cachedData = null;
    this.pendingRequest = null;
  }

  public updatePassword(current_password: string, new_password: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/users/update-password/`, { current_password, new_password });
  }
}
